import React, { useState, useEffect, useMemo } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import * as translator from "../../utils/translator";
import * as http from "../../utils/http";
import HeaderController from "../../components/Headers/HeaderController";
import "./CustomerVerification.css"
import {CartView as CartViewType} from '../../customHooks/useCart';

import StickyNotification from "../../components/StickyNotification/StickyNotification";
import Footer from "../../components/Footer/Footer";
import LoadingModal from "../../components/Modals/LoadingModal/LoadingModal";
import ServerError from "../../components/ServerError/ServerError";
import { useCartContext } from "../../customHooks/useCart";
import { MainTracker } from "../../PixelTrackers/MainTracker";
import usePhantomHistoryCleanup from "../../customHooks/usePhantomHistoryCleanup";

import { isPostCheckoutVerificationCountry } from "../../utils/postCheckoutVerification";

const MAX_RETRY_COUNT = 2

const CustomerVerification = () => {
    const navigate = useNavigate();
    let { country, language } = useParams();

    // GB/CH customers do Jumio post-purchase in My Account — redirect immediately,
    // before any render or server call, so the Jumio iframe never flashes.
    const isPostCheckoutCountry = isPostCheckoutVerificationCountry(country);

    const [workflowSrc, setWorkflowSrc] = useState<string>("");
    const [alreadyVerified, setAlreadyVerified] = useState<boolean>(false);
    const [retryCounter, setRetryCounter] = useState(0)
    const { getCart, saveCart } = useCartContext();
    const cart = getCart();


    const isSwissCannabis = country?.toUpperCase() === "CH" && cart?.mention?.includes("medical_cannabis");
    const hasProducts = cart?.products && cart.products.length > 0;

    const nextUrl = useMemo(() => {
        if (isSwissCannabis && !hasProducts) {
            return `/select_treatment/${country}/${language}/${cart?.mention}${window.location.search as any}`;
        }
        if (cart?.consultation_type === "video") {
            return `/choose-doctor/${country}/${language}/${cart?.mention}${window.location.search as any}`;
        }
        return `/shipping/${country}/${language}${window.location.search as any}`;
    }, [cart?.consultation_type, language, cart?.mention, isSwissCannabis, hasProducts]);


    const cleanupPhantomHistory = usePhantomHistoryCleanup();

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<any>(false);
    const [showStickyNotification, setShowStickyNotification] = useState({
        show: false,
        title: "",
        content: "",
        level: "error",
    });

    const getData = async () => {
        setLoading(true);
        http.getFromServer(`customer-verification/${country}/${language}`, [http.MIDDLEWARES.CUSTOMER_ID, http.MIDDLEWARES.CART_ORDER])
            .then((response) => {
                setWorkflowSrc(response.data.jumioWorkflowSrc);

                // save cart
                const responseData = response.data;
                const cartView = responseData.cartView as CartViewType;
                saveCart({...cartView, timekitBooking: cartView.timekitBooking || null});

            }).catch((error) => {
                const justVerified = new URLSearchParams(window.location.search).get('jvbj'); // used if customer verified in Jumio web, due to camera permissions
                if (error.response?.data?.status === "should_skip_jumio_verification" || justVerified === 'true') {
                    setAlreadyVerified(true);
                } else {
                    window.clientLogger.error('Error in CustomerVerification/getData', error, { code: "jumio_workflow_creation_failed"});
                    setError(500);
                }
                
                // save cart
                const responseData = error.response?.data;
                const cartView = responseData?.cartView as CartViewType;
                if (cartView) {
                    saveCart({...cartView, timekitBooking: cartView.timekitBooking || null});
                }


            }).finally(() => {
                setLoading(false);
            });
    }

    useEffect(() => {
        if (isPostCheckoutCountry) return;
        MainTracker.track("custom", 'ID verification viewed');
        getData();
    }, []);


    // handle message from jumio iFrame
    // on success, navigate to next url
    // on error, retry - recreate workflow
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            try {
                const data = event.data && typeof event.data === "string" ? JSON.parse(event.data) : null;

                if (data?.payload?.value === "loaded") {
                    // cleanup phantom history - the iframe adds extra history entries
                    cleanupPhantomHistory();
                }

                if (data?.payload?.value === "success") {
                    MainTracker.track("custom", 'ID verification completed');
                    const [nextPathname, nextSearch] = nextUrl.split("?");
                    navigate(
                        {pathname: nextPathname, search: nextSearch ? `?${nextSearch}` : undefined},
                        {replace: true}
                    );
                }

                if (data?.payload?.value === "error" && retryCounter < MAX_RETRY_COUNT) {
                    setRetryCounter(retryCounter + 1);
                    getData();
                }

                if (data?.payload?.value === "error") {
                    window.clientLogger.error("Error in jumio iFrame res", data)
                }
                
            } catch (error) {
                window.clientLogger.error("error in handleMessage from jumio", error);
            }
        };

        window.addEventListener("message", handleMessage);
        return () => {
            window.removeEventListener("message", handleMessage);
        };
    }, [cart]);

    if (isPostCheckoutCountry) {
        // nextUrl keeps the query string, which carries the Care+ marker ShippingV2 needs.
        return <Navigate to={nextUrl} replace />;
    }

    return (
        <>
            {loading && <LoadingModal />}

            {error && !loading && (
                <>
                    <HeaderController showSteps={false} stage={"CustomerVerification"} />
                    <ServerError status={500} />
                </>
            )}

            {!loading && !error && (
                <div className="customer-verification-page-wrapper">
                    <StickyNotification
                        showNotification={showStickyNotification.show}
                        title={showStickyNotification.title}
                        onClick={(falseValue: any) => setShowStickyNotification(falseValue)}
                        content={showStickyNotification.content}
                        level={showStickyNotification.level}
                    />

                    <HeaderController
                        language={language}
                        stage="CustomerVerification"
                    />

                    <div className="customer-verification-body-container">
                        {workflowSrc && !alreadyVerified && (
                            <div className="customer-verification-form-container">

                                <h1 className="customer-verification-title">{translator.translate(language, "customer_verification", "page/title")}</h1>
                                <br />
                                <p>{translator.translate(language, "customer_verification", "page/subtitle")}</p>
                                <br />
                                <p>{translator.translate(language, "customer_verification", "page/subtitle_2")}</p>


                                <div className="jumio-id-verification-container">
                                    <iframe
                                        className="jumio-iframe"
                                        src={workflowSrc}
                                        allow="camera;autoplay;fullscreen;clipboard-read;clipboard-write;accelerometer;gyroscope;magnetometer"
                                        title="ID Verification"
                                    ></iframe>
                                </div>
                            </div>
                        )}

                        {alreadyVerified && (
                            <div className="jumio-verified-message">
                                <div className="jumio-verified-message-inner">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="40"
                                        height="40"
                                        viewBox="0 0 40 40"
                                        fill="none"
                                        className="jumio-verified-message-icon"
                                    >
                                        <circle cx="20" cy="20" r="18" fill="#c6f3df" />
                                        <path d="M16.8 26.85 10.9 20.8l1.85-1.7 4.05 3.85 9.2-9.1 1.9 1.7Z" fill="#1fa366" />
                                    </svg>
                                    <span className="jumio-verified-message-text">
                                        {translator.translate(language, "customer_verification", "page/verified_message")}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    <Footer page="CustomerVerification" language={language} showFooterNext={alreadyVerified} onNextClick={() => navigate(nextUrl)} />
                </div>
            )}
        </>
    );
};



export default CustomerVerification;